import { useEffect, useRef, useState } from "react";
import { BookOpen } from "lucide-react";
import { qrApi, type QrSessione } from "@/api/qr";
import { QrTesta } from "./QrTesta";

declare global {
  interface Window {
    turnstile?: {
      render: (
        el: HTMLElement,
        opts: { sitekey: string; size?: string; callback: (token: string) => void },
      ) => string;
      remove?: (widgetId: string) => void;
    };
  }
}

const TURNSTILE_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js";
// I6: rete di sicurezza indipendente dall'esito del widget invisibile — se in
// Cloudflare il widget è "Managed" e decide di mostrare la spunta, la callback può
// non arrivare mai e «Avanti» resterebbe spento per sempre senza una parola.
const ATTESA_TOKEN_MS = 15_000;

interface Props {
  token: string;
  agenzia: string;
  turnstileSiteKey: string;
  onSessione: (s: QrSessione) => void;
}

/** Seconda schermata (spec §8 "Informativa"): casella obbligatoria + controllo
 *  anti-robot invisibile (Turnstile, §3.1 punto 2). «Avanti» crea la sessione. */
export function QrInformativa({ token, agenzia, turnstileSiteKey, onSessione }: Props) {
  const [checked, setChecked] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [erroreTurnstile, setErroreTurnstile] = useState(false);
  const [inviando, setInviando] = useState(false);
  const [tentativo, setTentativo] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);
  // Specchio sincrono di turnstileToken: il timer dei 15s deve leggere il valore
  // AL MOMENTO in cui scade, non quello catturato quando l'effetto è partito.
  const turnstileTokenRef = useRef<string | null>(null);

  function aggiornaTurnstileToken(t: string | null) {
    turnstileTokenRef.current = t;
    setTurnstileToken(t);
  }

  useEffect(() => {
    let cancelled = false;
    function render() {
      if (cancelled || !containerRef.current || !window.turnstile) return;
      // Il widget precedente (se c'è, dal tentativo scorso) va tolto prima di
      // crearne uno nuovo nello stesso contenitore: senza, resta appeso e
      // risponde ancora — due widget invisibili nello stesso posto.
      if (widgetIdRef.current) {
        window.turnstile.remove?.(widgetIdRef.current);
        widgetIdRef.current = null;
      }
      widgetIdRef.current = window.turnstile.render(containerRef.current, {
        sitekey: turnstileSiteKey,
        size: "invisible",
        callback: (t) => { if (!cancelled) aggiornaTurnstileToken(t); },
      });
    }
    if (window.turnstile) {
      render();
    } else {
      const script = document.createElement("script");
      script.src = TURNSTILE_SRC;
      script.async = true;
      script.defer = true;
      script.onload = render;
      document.body.appendChild(script);
    }
    return () => { cancelled = true; };
  }, [turnstileSiteKey, tentativo]);

  // I6: se dopo ATTESA_TOKEN_MS il token non è ancora arrivato, mostra lo stesso
  // errore/pulsante del caso "verifica fallita" (nessun testo nuovo). Riparte da
  // capo a ogni «Nuovo tentativo» (tentativo in dep, come l'effetto del widget).
  useEffect(() => {
    const timer = setTimeout(() => {
      if (turnstileTokenRef.current === null) setErroreTurnstile(true);
    }, ATTESA_TOKEN_MS);
    return () => clearTimeout(timer);
  }, [tentativo]);

  async function handleAvanti() {
    if (!checked || !turnstileToken || inviando) return;
    setInviando(true);
    setErroreTurnstile(false);
    try {
      const s = await qrApi.sessione(token, turnstileToken);
      onSessione(s);
    } catch {
      // Il token appena usato è consumato (o comunque sospetto): se restasse in
      // stato, «Avanti» resterebbe acceso finché l'utente non preme «Nuovo
      // tentativo» da solo, promettendo un secondo invio che fallirebbe di nuovo.
      aggiornaTurnstileToken(null);
      setErroreTurnstile(true);
    } finally {
      setInviando(false);
    }
  }

  function nuovoTentativo() {
    setErroreTurnstile(false);
    aggiornaTurnstileToken(null);
    setTentativo((n) => n + 1);
  }

  return (
    <div className="min-h-dvh flex flex-col bg-surface">
      <QrTesta agenzia={agenzia} />
      <div className="p-6 flex-1 flex flex-col w-full max-w-md mx-auto">
        <h1 className="font-heading text-2xl font-bold leading-tight">I suoi dati</h1>
        <p className="text-lg mt-3">
          Prima di caricare i documenti legga l&apos;informativa: spiega chi usa i suoi dati e perché.
        </p>
        <a
          href="/api/privacy/informativa/v1"
          target="_blank"
          rel="noopener noreferrer"
          className="mt-4 text-lg font-semibold text-brand-dark underline flex items-center gap-2 w-fit"
        >
          <BookOpen className="w-5 h-5" aria-hidden="true" />
          Informativa sulla privacy
        </a>
        <label className="mt-6 flex gap-3 items-start bg-card border-2 border-brand rounded-xl p-4 text-lg">
          <input
            type="checkbox"
            checked={checked}
            onChange={(e) => setChecked(e.target.checked)}
            className="mt-0.5 w-6 h-6 shrink-0 accent-brand"
          />
          Ho letto l&apos;informativa
        </label>
        <div ref={containerRef} />
        {erroreTurnstile && (
          <div className="mt-4 space-y-2" role="alert">
            <p className="text-lg text-red-700">
              Il controllo di sicurezza non è riuscito. Riprovi tra poco o chieda al banco.
            </p>
            <button type="button" onClick={nuovoTentativo} className="text-lg font-semibold text-brand-dark underline">
              Nuovo tentativo
            </button>
          </div>
        )}
        <button
          type="button"
          onClick={handleAvanti}
          disabled={!checked || !turnstileToken || inviando}
          className="mt-auto h-14 rounded-xl bg-brand text-white text-lg font-semibold disabled:opacity-40"
        >
          Avanti
        </button>
      </div>
    </div>
  );
}
